import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {alpha, Box, IconButton, useTheme} from '@mui/material'
import React, {useEffect, useRef, useState} from 'react'

import {useTranslation} from '../../../hooks/useTranslation'

// Heidelberg brand green used for the credentials box text and copy-button
// hover. Not exposed as a theme token (theme.palette.success.main is #1ac463),
// so kept as a single shared constant instead of repeated inline literals.
const HC_GREEN = '#007837'

type CloneSuccessToastProps = {
  userName?: string
  password?: string
  onClose: () => void
}

export const CloneSuccessToast = React.forwardRef<HTMLDivElement, CloneSuccessToastProps>(
  ({userName, password, onClose, ...rest}, ref) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const [copiedField, setCopiedField] = useState<'userName' | 'password' | null>(null)
    const resetTimer = useRef<ReturnType<typeof setTimeout>>()

    useEffect(
      () => () => {
        if (resetTimer.current) {
          clearTimeout(resetTimer.current)
        }
      },
      []
    )

    const handleCopy = (field: 'userName' | 'password', value?: string) => {
      if (!value || !navigator.clipboard?.writeText) {
        return
      }
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopiedField(field)
          if (resetTimer.current) {
            clearTimeout(resetTimer.current)
          }
          resetTimer.current = setTimeout(() => setCopiedField(null), 1500)
        })
        .catch(() => undefined)
    }

    const copyButtonSx = {
      ml: 0.5,
      p: 0.5,
      transition: 'background-color 0.15s ease-in-out',
      '&:hover': {
        backgroundColor: alpha(HC_GREEN, 0.12)
      }
    }

    return (
      <Box
        ref={ref}
        role="alert"
        {...rest}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          minWidth: 288,
          maxWidth: 420,
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
          fontSize: '0.875rem',
          lineHeight: 1.43,
          boxShadow: theme.shadows[6]
        }}
      >
        <CheckIcon sx={{fontSize: 20, mr: '10px', mt: 0.5, flexShrink: 0}} />
        <Box sx={{flexGrow: 1, minWidth: 0}}>
          <Box component="p" sx={{m: 0}}>
            {t('manageUser.cloneSuccess')}
          </Box>
          <Box
            sx={{
              mt: 2,
              mb: 1,
              p: 1.5,
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              color: HC_GREEN
            }}
          >
            <Box component="p" sx={{m: 0, display: 'flex', alignItems: 'center'}}>
              {t('manageUser.userName')}: {userName}
              <IconButton
                size="small"
                aria-label={t('manageUser.copyUserName')}
                color="inherit"
                data-test-id="button-clone-user-copy-username"
                onClick={() => handleCopy('userName', userName)}
                sx={copyButtonSx}
              >
                {copiedField === 'userName' ? (
                  <CheckIcon sx={{fontSize: 16}} />
                ) : (
                  <ContentCopyIcon sx={{fontSize: 16}} />
                )}
              </IconButton>
            </Box>
            <Box component="p" sx={{m: 0, mt: 0.5, display: 'flex', alignItems: 'center'}}>
              {t('manageUser.password')}: {password}
              <IconButton
                size="small"
                aria-label={t('manageUser.copyPassword')}
                color="inherit"
                data-test-id="button-clone-user-copy-password"
                onClick={() => handleCopy('password', password)}
                sx={copyButtonSx}
              >
                {copiedField === 'password' ? (
                  <CheckIcon sx={{fontSize: 16}} />
                ) : (
                  <ContentCopyIcon sx={{fontSize: 16}} />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
        <IconButton
          size="small"
          aria-label={t('manageUser.close')}
          color="inherit"
          data-test-id="button-clone-user-close-notification"
          onClick={onClose}
          sx={{
            ml: 1,
            p: 0.5,
            flexShrink: 0,
            transition: 'background-color 0.15s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    )
  }
)

CloneSuccessToast.displayName = 'CloneSuccessToast'
